/**
 * Persistent client-side queue for analytics events.
 *
 * Why this file exists
 * --------------------
 * Every `track()` call lands here BEFORE it is sent to the server. The queue
 * decouples event production from network delivery so analytics never blocks
 * the UI thread, never throws into caller code, and never loses events when
 * the network is flaky or the tab is closing.
 *
 * Storage model
 * -------------
 * - Primary store: IndexedDB (`mashlom-analytics` database, `events` object
 *   store). Survives page reloads and short offline periods.
 * - Fallback: an in-memory array, used only when IndexedDB is unavailable
 *   (private browsing in some browsers, corrupted IDB, etc).
 * - Each event is stored in EXACTLY ONE of these — never both. Earlier
 *   versions wrote to both and the in-memory copy was never cleared, which
 *   caused massive duplicate sends every time the tab lost visibility.
 *
 * Ring buffer
 * -----------
 * Capped at MAX_PERSISTED (500) records. When the queue overflows, the
 * oldest entries are dropped. This bounds disk/memory usage even if the
 * server stays unreachable for a long time.
 *
 * Send semantics — at-most-once with reinsertion on failure
 * ---------------------------------------------------------
 * `drainAndDelete()` reads up to N events AND deletes them in the same
 * IndexedDB transaction. The caller (track.ts → transport.ts) then attempts
 * to send the batch:
 *   - On send success: nothing else to do; the records are already gone.
 *   - On send failure: `reEnqueueMany()` reinserts them so the next flush
 *     will retry.
 *
 * This is "at-most-once at the queue layer + retry on caller failure" rather
 * than the common "read, send, then delete" pattern. The reason: read-then-
 * delete makes it impossible for a concurrent flush (e.g. the `pagehide`
 * beacon firing while the periodic fetch is mid-flight) to read the same
 * records twice. Worst case on send failure: a tiny window where events
 * are in transit but no longer in the queue. Reinsertion closes that.
 *
 * Concurrency
 * -----------
 * `track.ts` guards against concurrent flushes with `isFlushing` /
 * `isFlushingBeacon` flags. This file does not need its own concurrency
 * primitives — IndexedDB transactions are serialized within an origin and
 * the in-memory array is single-threaded by JS itself.
 *
 * What this file does NOT do
 * --------------------------
 * - Does not know about the network, fetch, or sendBeacon (that's transport.ts).
 * - Does not know about gtag (that's track.ts).
 * - Does not throw. Every error is swallowed; the queue degrades silently.
 */
import type { AnalyticsEventPayload } from './events';

const DB_NAME = 'mashlom-analytics';
const DB_VERSION = 1;
const STORE_NAME = 'events';

/** Hard cap on queue size — both IDB and in-memory. Oldest entries are dropped on overflow. */
const MAX_PERSISTED = 500;

/** Shape of a record stored in IDB. `_localId` is the auto-increment key, used internally for deletion. */
type QueuedRecord = AnalyticsEventPayload & { _localId: number };

/** Lazily opened, then cached for the lifetime of the page. `null` when IDB is unavailable. */
let dbPromise: Promise<IDBDatabase | null> | null = null;

const openDb = (): Promise<IDBDatabase | null> => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') {
        resolve(null);
        return;
      }
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: '_localId', autoIncrement: true });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
};

/** Fallback queue used only when IDB is unavailable. */
const memoryQueue: AnalyticsEventPayload[] = [];

/**
 * Add a single event to the queue. Returns immediately even if the underlying
 * write fails; the function never throws. Goes to IDB when available, otherwise
 * to the in-memory fallback. Trims the oldest entries when the queue overflows
 * MAX_PERSISTED.
 */
export const enqueue = async (event: AnalyticsEventPayload): Promise<void> => {
  try {
    const db = await openDb();
    if (!db) {
      memoryQueue.push(event);
      if (memoryQueue.length > MAX_PERSISTED) {
        memoryQueue.splice(0, memoryQueue.length - MAX_PERSISTED);
      }
      return;
    }
    await new Promise<void>((resolve) => {
      try {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        store.add({ ...event });
        const countReq = store.count();
        countReq.onsuccess = () => {
          if (countReq.result > MAX_PERSISTED) {
            const overflow = countReq.result - MAX_PERSISTED;
            const cursorReq = store.openCursor();
            let deleted = 0;
            cursorReq.onsuccess = () => {
              const cursor = cursorReq.result;
              if (cursor && deleted < overflow) {
                cursor.delete();
                deleted += 1;
                cursor.continue();
              }
            };
          }
        };
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
        tx.onabort = () => resolve();
      } catch {
        resolve();
      }
    });
  } catch {
    // swallow
  }
};

/**
 * Read up to `max` events from the queue AND delete them atomically in the
 * same transaction. Returns the events to the caller, who is responsible for
 * sending them and calling `reEnqueueMany` on failure.
 *
 * IDB readwrite transaction guarantees that a concurrent reader (e.g. the
 * beacon path firing during a pagehide while the periodic flush is mid-air)
 * can't see the same records — they're either drained already or visible.
 */
export const drainAndDelete = async (max: number): Promise<AnalyticsEventPayload[]> => {
  try {
    const db = await openDb();
    if (!db) {
      const events = memoryQueue.splice(0, Math.min(max, memoryQueue.length));
      return events;
    }
    return await new Promise<AnalyticsEventPayload[]>((resolve) => {
      try {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const events: AnalyticsEventPayload[] = [];
        const cursorReq = store.openCursor();
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (cursor && events.length < max) {
            const value = cursor.value as QueuedRecord;
            const copy: Partial<QueuedRecord> = { ...value };
            delete copy._localId;
            events.push(copy as AnalyticsEventPayload);
            cursor.delete();
            cursor.continue();
          }
        };
        tx.oncomplete = () => resolve(events);
        tx.onerror = () => resolve([]);
        tx.onabort = () => resolve([]);
      } catch {
        resolve([]);
      }
    });
  } catch {
    return [];
  }
};

/**
 * Reinsert events that were drained but couldn't be sent. Called by track.ts
 * when fetch/sendBeacon reports failure. The events go to the front of the
 * in-memory queue (so they retry first) or get re-added to IDB.
 *
 * Events carry no timestamp of their own — the server stamps them on arrival —
 * so a retried event is dated when it finally lands, not when it happened.
 */
export const reEnqueueMany = async (events: AnalyticsEventPayload[]): Promise<void> => {
  if (events.length === 0) return;
  try {
    const db = await openDb();
    if (!db) {
      memoryQueue.unshift(...events);
      if (memoryQueue.length > MAX_PERSISTED) {
        memoryQueue.splice(0, memoryQueue.length - MAX_PERSISTED);
      }
      return;
    }
    await new Promise<void>((resolve) => {
      try {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        events.forEach((event) => store.add({ ...event }));
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
        tx.onabort = () => resolve();
      } catch {
        resolve();
      }
    });
  } catch {
    // swallow
  }
};
